import { useState, memo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { optimizeImageUrl } from "@/utils/imageOptimizer";

const ModifierModal = ({ open, onClose, item, onConfirm, initialModifiers, initialNotes, editMode }) => {
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [notes, setNotes] = useState("");
  const [prevItemId, setPrevItemId] = useState(null);

  // Reset modifier selection when a new item is opened (React 18 idiom:
  // derive during render rather than via useEffect). Avoids the previous
  // item's modifiers leaking into the next. When editing an existing ticket
  // item, seed selection + notes from the caller so the picker reflects the
  // current line rather than starting empty.
  if (open && item?.id !== prevItemId) {
    setPrevItemId(item?.id);
    // Seed selection from ``initialModifiers`` (edit mode) — tag each
    // modifier with its owning ``_group`` so the radio/max-select rules
    // behave sanely (e.g. picking a different size deselects the old one).
    const seed = (initialModifiers || []).map(m => {
      const g = (item?.modifier_groups || []).find(gr =>
        (gr.options || []).some(o => o.name === m.name)
      );
      return { name: m.name, price: m.price || 0, ...(g ? { _group: g.group_name } : {}) };
    });
    setSelectedModifiers(seed);
    setNotes(initialNotes || "");
  }

  if (!item) return null;

  const groups = item.modifier_groups || [];
  // Fallback: if no groups but has legacy modifiers
  const legacyModifiers = item.modifiers || [];
  const hasGroups = groups.length > 0;
  const hasModifiers = hasGroups || legacyModifiers.length > 0;

  const handleGroupToggle = (groupName, option, maxSelect) => {
    setSelectedModifiers(prev => {
      const withoutThisOption = prev.filter(m => !(m._group === groupName && m.name === option.name));
      const inGroup = prev.filter(m => m._group === groupName);
      const alreadySelected = prev.some(m => m._group === groupName && m.name === option.name);

      if (alreadySelected) {
        return withoutThisOption;
      }

      // If max_select is 1, replace the existing selection in this group (radio behavior)
      if (maxSelect === 1) {
        return [...prev.filter(m => m._group !== groupName), { ...option, _group: groupName }];
      }

      // If max reached, don't add
      if (maxSelect > 0 && inGroup.length >= maxSelect) {
        return prev;
      }

      return [...prev, { ...option, _group: groupName }];
    });
  };

  const handleLegacyToggle = (modifier) => {
    setSelectedModifiers(prev => {
      const exists = prev.find(m => !m._group && m.name === modifier.name);
      return exists
        ? prev.filter(m => !(!m._group && m.name === modifier.name))
        : [...prev, { name: modifier.name, price: modifier.price || 0 }];
    });
  };

  const handleConfirm = () => {
    // Enforce required modifier groups server-side of the modal — the user
    // can't click "Add to Order" without picking at least one option from
    // every group marked ``required``. Highlighting is driven by
    // ``missingRequiredGroups`` below; this final guard is what actually
    // blocks submit in case a stale click gets through.
    if (missingRequiredGroups.length > 0) {
      toast.error(`Please choose: ${missingRequiredGroups.join(", ")}`);
      return;
    }
    // Strip _group from modifiers before sending
    const clean = selectedModifiers.map(({ _group, ...rest }) => rest);
    onConfirm(clean, notes);
  };

  // Groups whose current selection count is below their effective minimum.
  // ``min_select`` takes precedence; legacy ``required=true`` maps to min=1.
  // Recomputed on every render — cheap for a modal with < 10 groups.
  const _groupMissing = (g) => {
    const min_needed = Math.max(
      parseInt(g.min_select) || 0,
      g.required ? 1 : 0,
    );
    if (min_needed <= 0) return 0;
    const picked = selectedModifiers.filter(m => m._group === g.group_name).length;
    return Math.max(0, min_needed - picked);
  };
  const missingRequiredGroups = groups
    .filter(g => _groupMissing(g) > 0)
    .map(g => {
      const needed = Math.max(parseInt(g.min_select) || 0, g.required ? 1 : 0);
      return needed > 1 ? `${g.group_name} (${_groupMissing(g)} more)` : g.group_name;
    });
  const canSubmit = missingRequiredGroups.length === 0;

  // No local state reset here — the derived-state check at the top of the
  // component handles cleanup when a *different* item is opened next time.
  // Skipping the reset eliminates a wasted re-render of the modal with
  // empty state before it unmounts (perceived as close lag on slow CPUs).
  const handleClose = () => onClose();

  const totalPrice = item.price + selectedModifiers.reduce((sum, m) => sum + (m.price || 0), 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px] max-h-[80vh] overflow-y-auto" data-testid="modifier-modal">
        <DialogHeader><DialogTitle className="font-heading text-xl">{editMode ? `Edit — ${item.name}` : item.name}</DialogTitle></DialogHeader>
        <div className="py-2">
          {item.image_url && (
            <div className="aspect-video rounded-lg overflow-hidden bg-secondary mb-4">
              <img
                src={optimizeImageUrl(item.image_url, 420)}
                alt={item.name}
                width={420}
                height={236}
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-4">{item.description}</p>

          {/* Grouped Modifiers */}
          {hasGroups && groups.map((group, gIdx) => {
            const isMissing = missingRequiredGroups.some(m => m === group.group_name || m.startsWith(`${group.group_name} (`));
            const minN = Math.max(parseInt(group.min_select) || 0, group.required ? 1 : 0);
            const maxN = parseInt(group.max_select) || 0;
            let constraint = "";
            if (minN > 0 && maxN > 0) constraint = minN === maxN ? `Pick ${minN}` : `Pick ${minN}–${maxN}`;
            else if (minN > 0)         constraint = minN === 1 ? "" : `Pick at least ${minN}`;
            else if (maxN === 1)       constraint = "Pick 1";
            else if (maxN > 1)         constraint = `Max ${maxN}`;
            return (
            <div
              key={gIdx}
              className={`mb-4 rounded-lg transition-colors ${isMissing ? "ring-1 ring-destructive/40 bg-destructive/5 p-3 -mx-1" : ""}`}
              data-testid={`group-${gIdx}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Label className="text-sm font-medium">{group.group_name}</Label>
                {minN > 0 && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] h-5 px-1.5 border-destructive/30 ${isMissing ? "bg-destructive text-white border-destructive" : "text-destructive"}`}
                    data-testid={`required-badge-${gIdx}`}
                  >
                    Required{isMissing ? " — pick " + (minN > 1 ? `${minN}` : "one") : ""}
                  </Badge>
                )}
                {constraint && <Badge variant="outline" className="text-[10px] h-5 px-1.5">{constraint}</Badge>}
              </div>
              <div className="space-y-1.5">
                {(group.options || []).map((opt, oIdx) => {
                  const isSelected = selectedModifiers.some(m => m._group === group.group_name && m.name === opt.name);
                  return (
                    <div
                      key={oIdx}
                      className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-colors ${isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}
                      onClick={() => handleGroupToggle(group.group_name, opt, group.max_select)}
                      data-testid={`modifier-${gIdx}-${oIdx}`}
                    >
                      <div className="flex items-center gap-3">
                        {group.max_select === 1 ? (
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-primary" : "border-muted-foreground/30"}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                          </div>
                        ) : (
                          <Checkbox checked={isSelected} />
                        )}
                        <span className="text-sm">{opt.name}</span>
                      </div>
                      {opt.price > 0 && <span className="text-sm font-mono text-muted-foreground">+${opt.price.toFixed(2)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}

          {/* Item-specific modifiers — always shown when present so servers
              can layer one-off add-ons on top of any grouped options. */}
          {legacyModifiers.length > 0 && (
            <div className="mb-4">
              <Label className="text-sm font-medium mb-2 block">{hasGroups ? "Extras" : "Customize"}</Label>
              <div className="space-y-1.5">
                {legacyModifiers.map((modifier, index) => (
                  <div key={index} className="flex items-center justify-between p-2.5 rounded-lg border border-border hover:bg-secondary/50 cursor-pointer" onClick={() => handleLegacyToggle(modifier)} data-testid={`modifier-legacy-${index}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={selectedModifiers.some(m => !m._group && m.name === modifier.name)} />
                      <span className="text-sm">{modifier.name}</span>
                    </div>
                    {modifier.price > 0 && <span className="text-sm font-mono text-muted-foreground">+${modifier.price.toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Special Instructions</Label>
            <Textarea placeholder="Any special requests..." value={notes} onChange={(e) => setNotes(e.target.value)} className="resize-none" rows={2} data-testid="item-notes" />
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="font-mono text-lg font-medium">${totalPrice.toFixed(2)}</span>
            {!canSubmit && (
              <span className="text-[11px] text-destructive" data-testid="required-hint">
                Missing: {missingRequiredGroups.join(", ")}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={!canSubmit}
              data-testid="add-item-btn"
              title={canSubmit ? "" : `Please choose: ${missingRequiredGroups.join(", ")}`}
            >
              {editMode ? "Save Changes" : "Add to Order"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ModifierModal);
